import L from 'leaflet';
import Line from '../components/zone/line.js';
import Polygon from '../components/zone/polygon.js';
import Point from '../components/zone/point.js';
import LineEdit from '../components/zone/lineEdit';
import PolygonEdit from '../components/zone/polygonEdit';
import PointEdit from '../components/zone/pointEdit';

export default class GeoService {
    constructor(map, settings) {
        this.map = map;
        this.path = '../../../';
        this.geoLayer = null;
        this.isGeozonesVisible = settings.geozonesVisibility;
        this.changeGeofencePoints = null;
        this.editGeofenceLayer = null;
        this.fnSelectGeofence = null;
        this.geofencesLayersByIds = {};
        this.allGeofencesLayer = null;

        const markerPane = this.map.getPane('markerPane');
        const clickStopperPane = L.DomUtil.create('div', 'click-stopper-pane');
        this.map.on(L.Draw.Event.DRAWSTART, function () {
            markerPane.insertBefore(clickStopperPane, markerPane.children[0]);
        }).on(L.Draw.Event.DRAWSTOP, function () {
            markerPane.removeChild(clickStopperPane);
        });
    }

    setGeozonesVisibility = () => {
        if (this.isGeozonesVisible) {
            this.map.mapRemoveLayer(this.allGeofencesLayer);
        }
        else {
            this.addGeozonesLayer();
        }
        this.isGeozonesVisible = !this.isGeozonesVisible;
        return this.isGeozonesVisible;
    }

    updateEditingGeofence(data) {
        this.editGeofenceLayer.updateData(data);
    }

    addGeozonesLayer() {
        this.map.addLayer(this.allGeofencesLayer);
    }

    setGeofences(geofencesByIds, fnSelectGeofence) {
        this.allGeofencesLayer = undefined;
        this.fnSelectGeofence = fnSelectGeofence;
        let geofenceLayers = [];
        for (let id in geofencesByIds) {
            let geofenceData = geofencesByIds[id];
            if (geofenceData.dataForMap.showOnMap) {
                let geofenceLayer = this.createGeofence(geofenceData, this.fnSelectGeofence);
                this.geofencesLayersByIds[id] = geofenceLayer;
                geofenceLayers.push(geofenceLayer);
            }
        }
        this.allGeofencesLayer = L.featureGroup(geofenceLayers, { clusterPane: 'zonesPane' });
        if (this.isGeozonesVisible) {
            this.map.addLayer(this.allGeofencesLayer);
        }
    }

    addNewGeofencesToExistingLayer(geofencesByIds) {
        for (let id in geofencesByIds) {
            let geofenceData = geofencesByIds[id];
            if (geofenceData.dataForMap.showOnMap) {
                let geofenceLayer = this.createGeofence(geofenceData, this.fnSelectGeofence);
                this.geofencesLayersByIds[id] = geofenceLayer;
                geofenceLayer.addTo(this.allGeofencesLayer);
            }
        }
    }

    selectGeofence(id) {
        let selectedGeoLayer = this.geofencesLayersByIds[id];
        if (selectedGeoLayer.type == "geopoint") {
            let zoom = this.map.getZoom();
            zoom = zoom < 15 ? 15 : zoom;
            return this.map.setView(selectedGeoLayer.getLatLng(), zoom);
        }
        return this.map.fitBounds(selectedGeoLayer.getBounds(), { padding: [100, 100] });
    }

    updateGeofences(geofencesByIds) {
        for (let id in geofencesByIds) {
            this.map.mapRemoveLayer(this.geofencesLayersByIds[id]);
            let geofenceData = geofencesByIds[id];
            if (geofenceData.dataForMap.showOnMap) {
                let geofenceLayer = this.createGeofence(geofenceData, fnSelectGeofence);
                this.geofencesLayersByIds[id] = geofenceLayer;
                geofenceLayer.addTo(this.allGeofencesLayer);
            }
        }
    }

    deleteGeofences(ids) {
        for (let id of ids) {
            let layerToRemove = this.geofencesLayersByIds[id];
            this.allGeofencesLayer.removeLayer(layerToRemove);
            delete this.geofencesLayersByIds[id];
        }
        this.map.closePopup();
    }

    deleteAllGeofences = () => {
        let allGeoIds = Object.keys(this.geofencesLayersByIds);
        this.deleteGeofences(allGeoIds);
    }

    startEditGeofence(geofenceData, changeGeofencePoints, isOptimizing = false) {
        this.map.isEditingGeofence && this.finishEditGeofence();
        this.map.closePopup();
        this.changeGeofencePoints = changeGeofencePoints;
        if (this.geofencesLayersByIds[geofenceData.id]) {
            this.map.mapRemoveLayer(this.geofencesLayersByIds[geofenceData.id]);
        }
        this.editGeofenceLayer = this.createEditedGeofence(geofenceData, null);
        this.map.addLayer(this.editGeofenceLayer);
        this.map.isEditingGeofence = true;
        this.editGeofenceLayer.startEdit();
        let event = new CustomEvent('geofence:startEdit');
        document.dispatchEvent(event);
        if (isOptimizing) {
            this.editGeofenceLayer.dragging.disable();
        }
    }

    finishEditGeofence() {
        if (this.editGeofenceLayer) {
            this.map.mapRemoveLayer(this.editGeofenceLayer);
            if (this.geofencesLayersByIds[this.editGeofenceLayer.id]) {
                this.map.addLayer(this.geofencesLayersByIds[this.editGeofenceLayer.id]);
            }
            this.map.isEditingGeofence = false;
            this.editGeofenceLayer = null;
        }
    }

    createGeofence(geofenceData, fnSelectGeofence) {
        switch (geofenceData.type) {
            case 'geopolygon': {
                return new Polygon(this.map, geofenceData, fnSelectGeofence);
            }
            case 'geoline': {
                return new Line(this.map, geofenceData, fnSelectGeofence);
            }
            case 'geopoint': {
                return new Point(this.map, geofenceData, fnSelectGeofence);
            }
        }
    }

    createEditedGeofence(geofenceData) {
        switch (geofenceData.type) {
            case 'geopolygon': {
                return new PolygonEdit(this.map, geofenceData, this.changeGeofencePoints);
            }
            case 'geoline': {
                return new LineEdit(this.map, geofenceData, this.changeGeofencePoints);
            }
            case 'geopoint': {
                return new PointEdit(this.map, geofenceData, this.changeGeofencePoints);
            }
        }
    }
}
